import React, { useMemo, useState } from "react";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { isEmpty } from "lodash";

import ConfigurationRunDock from "./ConfigurationRunDock";
import InThisRunPanel from "./InThisRunPanel";
import ReadinessChecklist from "./ReadinessChecklist";
import FuelPrices from "./FuelPrices";
import { Section, TableHeader, TableRow } from "./SimulationInfo";

import { getLabel } from "utils/dcfExport/details";
import { getSortableName } from "utils/directory/sorting";
import { checkIsSimulationRunning } from "utils/communities";
import { startCommunityParametricRun } from "utils/communities/api";
import { getCommunityMeasureValidationStats } from "utils/communities/communityValidation";
import { getCommunityRunDisabledReason } from "utils/communities/communityReadiness";
import { useStateSelector } from "utils/field";
import { getMissingEcmInputMapCodes, getTableUpgradesMissingFromEcmInputMap } from "utils/upgrades/ecmInputMap";
import {
    getCommunityValidationComboCount,
    COMMUNITY_VALIDATION_COMBO_MIN,
    COMMUNITY_VALIDATION_COMBO_MAX,
} from "utils/communities/validationComboCount";
import { useCommunityValidationLock } from "../CommunityValidationLockContext";
import { useCommunityRunStats } from "../hooks/useCommunityRunStats";

import { fetchCommunityRunHistory, saveCommunity, saveEcmInputMap } from "store/community/thunk";
import { saveUpgrades } from "../Upgrades/_ducks/thunk";
import { selectUser } from "store/users/selectors";
import {
    selectCommunityDetails,
    selectCommunityEcmInputMap,
    selectCommunityModelDir,
    selectCommunityRunHistory,
    selectModelUpgradesRun,
} from "store/community/selectors";
import { selectUpgradePackagesSortedArray } from "../Upgrades/_ducks/selectors";
import commParametricRunConfig from "../templates/commParametricRunConfig.json";

import classes from "./style.module.scss";
import sharedClasses from "../style.module.scss";
import Upgrades from "assets/images/icons/JSX/Upgrades";
import House from "assets/images/icons/JSX/House";
import Ready from "assets/images/icons/Ready.svg";
import Error from "assets/images/icons/warning.svg";
import Unknown from "assets/images/icons/warning-yellow.svg";

const ecmSyncIssueDescription = (issue) => {
    const base = `${issue.packageName}: ${issue.labelText}`;
    if (issue.reason === "package_id_mismatch") {
        return `${base} — ECM row points to a different package; re-apply, edit, or remove this upgrade.`;
    }
    if (issue.reason === "missing_category") {
        return `${base} — measure category is missing; edit or remove this row.`;
    }
    if (issue.reason === "missing_ecm_row") {
        return `${base} — not represented in the ECM map; re-apply, edit, or remove.`;
    }
    return `${base} — re-apply, edit, or remove this upgrade.`;
};

const Configuration = ({
    history,
    communityId,
    uid,
    user,
    modelDir: { models = {} },
    communityDetails: { availableRuns = 0 } = {},
    ecmInputMap = {},
    upgradePackages = [],
    runHistory = {},
    modelUpgradesRun = {},
    fetchCommunityRunHistory,
    saveCommunityUpgrades,
    saveCommunityData,
    saveEcmInputMap,
    onUnlockRunsClick,
}) => {
    const { preferredUnits = "Metric" } = useStateSelector("user.userPreferences") || {};
    const { validationUiLocked, validationStarting } = useCommunityValidationLock();

    const [isPreparingRun, toggleIsPreparingRun] = useState(false);

    const measureStats = useMemo(
        () => getCommunityMeasureValidationStats(ecmInputMap, upgradePackages),
        [ecmInputMap, upgradePackages]
    );

    const upgradePackagesMap = useMemo(
        () => upgradePackages.reduce((acc, pkg) => ({ ...acc, [pkg.id]: pkg }), {}),
        [upgradePackages]
    );

    const ecmTableSyncIssues = useMemo(
        () => getTableUpgradesMissingFromEcmInputMap(upgradePackagesMap, ecmInputMap),
        [upgradePackagesMap, ecmInputMap]
    );

    const validationComboOutOfRange = useMemo(() => {
        const count = getCommunityValidationComboCount(ecmInputMap);
        return count < COMMUNITY_VALIDATION_COMBO_MIN || count > COMMUNITY_VALIDATION_COMBO_MAX;
    }, [ecmInputMap]);

    const sortedModels = Object.entries(models).sort(([_, aValues], [__, bValues]) => {
        if (aValues.status === "ready" && bValues.status !== "ready") return -1;
        if (aValues.status !== "ready" && bValues.status === "ready") return 1;
        return getSortableName(aValues?.name).localeCompare(getSortableName(bValues?.name));
    });

    const readyModels = sortedModels.filter(([, modelObj]) => modelObj.status === "ready");
    let uniqueModels = 0;
    readyModels.forEach(([, modelObj]) => {
        uniqueModels += Object.values(modelObj.countDirection.copyDirections).filter((val) => val > 0).length;
    });

    const { runInfo, runsUsedTotal, numBaseFiles, numUpgradePackages } = useCommunityRunStats({
        communityId,
        fetchCommunityRunHistory,
        readyModels,
        upgradePackages,
        ecmInputMap,
        modelUpgradesRun,
        runHistory,
    });

    const { numFailed, numToBeRun, totalSimulations } = runInfo;

    const insufficientRuns = (totalSimulations || 0) > availableRuns;

    const missingCodes = useMemo(
        () => getMissingEcmInputMapCodes(upgradePackages, ecmInputMap),
        [upgradePackages, ecmInputMap]
    );

    const isSimulationRunning = useMemo(() => checkIsSimulationRunning(runHistory), [runHistory]);

    const runDisabledReason = getCommunityRunDisabledReason({
        isSimulationRunning,
        isPreparingRun,
        validationUiLocked,
        validationStarting,
        numToBeRun,
        hasMissingCodes: !isEmpty(missingCodes),
        ecmTableSyncIssues,
        measureStats,
        insufficientRuns,
        numUpgradePackages,
    });

    const runDisabled = Boolean(runDisabledReason);

    const persistCommunityForSimulation = async () => {
        await saveCommunityData(uid);
        await saveCommunityUpgrades();
        return saveEcmInputMap(communityId);
    };

    const handleCommunityParametricRunClick = async () => {
        toggleIsPreparingRun(true);
        try {
            const savedMap = await persistCommunityForSimulation();
            const mapToSend = savedMap || ecmInputMap;

            if (communityId && numUpgradePackages >= 1) {
                const packagesMap = upgradePackages.reduce(
                    (acc, curr) => ({
                        ...acc,
                        [curr.id]: curr,
                    }),
                    {}
                );

                const resultsWholePackages = await startCommunityParametricRun({
                    communityId,
                    models,
                    upgradePackages: packagesMap,
                    parametricRunConfig: {
                        ...commParametricRunConfig,
                        user: { uid },
                        ecmInputMap: mapToSend,
                        buildType: "commWholePackages",
                    },
                    initModelUpgradesRun: modelUpgradesRun,
                });

                if (!resultsWholePackages.ok) {
                    window.alert(
                        resultsWholePackages.message ||
                            `Community run could not start${resultsWholePackages.status ? ` (HTTP ${resultsWholePackages.status})` : ""}.`
                    );
                    return;
                }

                const resultsByMeasure = await startCommunityParametricRun({
                    communityId,
                    models,
                    upgradePackages: packagesMap,
                    parametricRunConfig: {
                        ...commParametricRunConfig,
                        user: { uid },
                        ecmInputMap: mapToSend,
                        buildType: "commByMeasure",
                    },
                    initModelUpgradesRun: modelUpgradesRun,
                });

                if (!resultsByMeasure.ok) {
                    window.alert(
                        resultsByMeasure.message ||
                            `Community run could not start${resultsByMeasure.status ? ` (HTTP ${resultsByMeasure.status})` : ""}.`
                    );
                    return;
                }

                if (resultsWholePackages.status === 202 && resultsByMeasure.status === 202) {
                    history.push(`/communities/${communityId}/results`);
                }
            }
        } finally {
            toggleIsPreparingRun(false);
        }
    };

    const getDirectionLabel = (shortHand) => {
        const index = ["S", "SE", "E", "NE", "N", "NW", "W", "SW"].indexOf(shortHand);
        return getLabel("facingDirection", index);
    };

    return (
        <div className={sharedClasses.contentContainer}>
            <ConfigurationRunDock
                totalSimulations={totalSimulations}
                numToBeRun={numToBeRun}
                availableRuns={availableRuns}
                insufficientRuns={insufficientRuns}
                runDisabled={runDisabled}
                runDisabledReason={runDisabledReason}
                isSimulationRunning={isSimulationRunning}
                isPreparingRun={isPreparingRun}
                onUnlockRunsClick={onUnlockRunsClick}
                communityId={communityId}
                measureStats={measureStats}
                handleCommunityParametricRunClick={handleCommunityParametricRunClick}
                hasMissingCodes={!isEmpty(missingCodes)}
                ecmTableSyncIssues={ecmTableSyncIssues}
                validationComboOutOfRange={validationComboOutOfRange}
                validationInProgress={validationUiLocked}
                validationStarting={validationStarting}
            />

            <div className={classes.readinessRow}>
                <ReadinessChecklist
                    hasMissingCodes={!isEmpty(missingCodes)}
                    ecmTableSyncIssues={ecmTableSyncIssues}
                    measureStats={measureStats}
                    validationComboOutOfRange={validationComboOutOfRange}
                    insufficientRuns={insufficientRuns}
                    validationInProgress={validationUiLocked || validationStarting}
                    failingLabels={measureStats.failingLabels}
                    unvalidatedLabels={measureStats.unvalidatedLabels}
                    ecmSyncIssueDescription={ecmSyncIssueDescription}
                />
                <InThisRunPanel
                    numBaseFiles={numBaseFiles}
                    uniqueModels={uniqueModels}
                    numUpgradePackages={numUpgradePackages}
                    totalSimulations={totalSimulations}
                />
            </div>

            {!!numFailed && (
                <div className={classes.section}>
                    <p className={classes.simulationLeftLine}>
                        <b>{numFailed} prior simulation(s) failed</b> — see Results for details.
                    </p>
                </div>
            )}

            <div className={classes.inventoryGrid}>
                <Section
                    className={classes.inventoryPanel}
                    title={`${uniqueModels} Unique Models (${numBaseFiles} Total Models)`}
                    bodyClassName={classes.inventoryScroll}
                >
                    <TableHeader gridTemplateColumns="1fr 1fr 0.25fr">
                        <p>Base Model Name</p>
                        <p>Duplicates</p>
                        <p className={classes.status}>Status</p>
                    </TableHeader>

                    {sortedModels.map(([id, model]) => {
                        const copyDirections = Object.entries(model.countDirection.copyDirections).filter(
                            ([, value]) => value > 0
                        );
                        return (
                            <TableRow
                                id={`base-model-${id}`}
                                key={`base-model-${id}`}
                                disabled={model.status !== "ready"}
                                gridTemplateColumns="1fr 1fr 0.25fr"
                            >
                                <div className={classes.itemTitle}>
                                    <House color={"#f58020"} /> <p>{model.name}</p>
                                </div>
                                <p className={classes.countDirection}>
                                    {copyDirections.map(([key, value], index) => (
                                        <span id={`${id}-${key}`} key={`${id}-${key}`}>
                                            {value} {getDirectionLabel(key)}
                                            {index < copyDirections.length - 1 ? "," : ""}
                                            {index === 3 ? <br /> : " "}
                                        </span>
                                    ))}
                                </p>
                                <div className={classes.status}>
                                    <img
                                        src={
                                            model.status === "ready"
                                                ? Ready
                                                : model.status === "error"
                                                  ? Error
                                                  : model.status === "unknown"
                                                    ? Unknown
                                                    : ""
                                        }
                                        alt=""
                                    />
                                </div>
                            </TableRow>
                        );
                    })}
                </Section>

                <Section
                    className={classes.inventoryPanel}
                    title={`${numUpgradePackages} Upgrade Packages (${numUpgradePackages + 1} Total Packages)`}
                    bodyClassName={classes.inventoryScroll}
                >
                    <TableHeader gridTemplateColumns="1fr">
                        <p>Upgrade Package Name</p>
                    </TableHeader>
                    <TableRow gridTemplateColumns="1fr">
                        <p className={classes.itemTitle}>
                            <House color={"#92a4ad"} /> Base Case
                        </p>
                    </TableRow>
                    {upgradePackages.map((upgrade) => (
                        <TableRow key={`package-${upgrade.name}`} gridTemplateColumns="1fr">
                            <p className={classes.itemTitle}>
                                <Upgrades color={"#f58020"} /> {upgrade.name}
                            </p>
                        </TableRow>
                    ))}
                </Section>
            </div>

            <FuelPrices modelUnits={preferredUnits} />
        </div>
    );
};

const mapStateToProps = createStructuredSelector({
    user: selectUser,
    communityDetails: selectCommunityDetails,
    modelDir: selectCommunityModelDir,
    ecmInputMap: selectCommunityEcmInputMap,
    upgradePackages: selectUpgradePackagesSortedArray,
    runHistory: selectCommunityRunHistory,
    modelUpgradesRun: selectModelUpgradesRun,
});

const mapDispatchToProps = (dispatch) => ({
    fetchCommunityRunHistory: async (communityId) => dispatch(fetchCommunityRunHistory(communityId)),
    saveCommunityUpgrades: () => dispatch(saveUpgrades()),
    saveCommunityData: (uid) => dispatch(saveCommunity(uid)),
    saveEcmInputMap: (id) => dispatch(saveEcmInputMap(id)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Configuration);
